import React, { useEffect, useRef, useState } from "react";

import { deleteComment, deleteEvent, deletePost } from "../../api/api";
import { useAdminModeration } from "../../context/AdminModerationContext";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";

/**
 * Whether the signed-in account authored this post, event, or comment.
 * @param {number} userId
 * @param {object} [user]
 * @param {object} item
 * @returns {boolean}
 */
export function isOwnContent(userId, user, item) {
  if (!userId || item?.author_id == null) {
    return false;
  }
  const viewerIsOrg = user?.type === "organization";
  return (
    Number(userId) === Number(item.author_id) &&
    Boolean(viewerIsOrg) === Boolean(item.author_is_org)
  );
}

/**
 * Vertical-dots glyph for the overflow trigger.
 * @param {{ className?: string }} [props]
 * @returns {JSX.Element}
 */
function IconMoreVertical({ className = "h-[18px] w-[18px]" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

const COPY = {
  post: {
    title: "Delete post?",
    description:
      "This removes your post from the feed. Comments on it will also be removed. This cannot be undone.",
  },
  event: {
    title: "Delete event?",
    description:
      "This removes your event from the feed. This cannot be undone.",
  },
  comment: {
    title: "Delete comment?",
    description:
      "This removes your comment and its replies from the thread. This cannot be undone.",
  },
  reply: {
    title: "Delete reply?",
    description:
      "This removes your reply from the thread. This cannot be undone.",
  },
};

/**
 * Owner-only ⋯ menu with a confirm dialog for soft-deleting feed content.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} [props.user]
 * @param {object} props.item
 * @param {"post"|"event"|"comment"|"reply"} props.contentType
 * @param {() => void} [props.onDeleted]
 * @param {string} [props.triggerClassName]
 * @returns {JSX.Element|null}
 */
export default function ContentOverflowMenu({
  userId,
  user,
  item,
  contentType,
  onDeleted,
  triggerClassName,
}) {
  const moderation = useAdminModeration();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    function handlePointerDown(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [menuOpen]);

  if ((!moderation && !isOwnContent(userId, user, item)) || !item?.id) {
    return null;
  }

  const copy = COPY[contentType] || COPY.post;

  /**
   * Soft-delete the item, then notify the parent to hide it.
   * @returns {Promise<void>}
   */
  async function handleConfirm() {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      let result;
      if (contentType === "event") {
        result = await deleteEvent(item.id);
      } else if (contentType === "comment" || contentType === "reply") {
        result = await deleteComment(item.id);
      } else {
        result = await deletePost(item.id);
      }
      if (result?.success === false || result?.error) {
        console.error(result?.error || result?.message || "Failed to delete");
        return;
      }
      setConfirmOpen(false);
      onDeleted?.();
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="relative shrink-0" ref={wrapRef}>
        <button
          type="button"
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring",
            triggerClassName
          )}
          aria-label="More actions"
          aria-expanded={menuOpen}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setMenuOpen((open) => !open);
          }}
        >
          <IconMoreVertical />
        </button>
        {menuOpen ? (
          <div
            className={cn(
              "absolute right-0 z-30 min-w-[9rem] overflow-hidden rounded-md border border-ui-border bg-ui-card text-sm shadow-md",
              "top-full mt-1"
            )}
          >
            <div className="py-1">
              <button
                type="button"
                className="flex w-full items-center px-3 py-2 text-left text-delete hover:bg-highlight"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setMenuOpen(false);
                  if (moderation) {
                    moderation.onRequestDelete({
                      contentType,
                      item,
                      onSuccess: () => onDeleted?.(),
                    });
                    return;
                  }
                  setConfirmOpen(true);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!busy) {
            setConfirmOpen(open);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={handleConfirm}
            >
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
